/**
 * React Query bindings for the media library (`apps.assets`).
 *
 * Cache-invalidation rule of thumb: anything that changes bytes or metadata
 * must also refresh `assetStats` (the header counters) and, when it can change
 * whether a file is referenced, `assetUsage`. Folder writes touch the asset
 * list too — moving/deleting a folder re-buckets assets.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { getErrorMessage } from '@/services/api';
import {
  assetsService,
  asAssetInUseConflict,
  type AssetListParams,
  type AssetMetadataPayload,
  type AssetReplacePayload,
  type FolderPayload,
} from '@/services/assets';

const ASSETS = 'assets';

export const assetKeys = {
  all: [ASSETS] as const,
  list: (params: AssetListParams) => [ASSETS, 'list', params] as const,
  detail: (id: string) => [ASSETS, 'detail', id] as const,
  usage: (id: string) => [ASSETS, 'usage', id] as const,
  stats: (params: AssetListParams) => [ASSETS, 'stats', params] as const,
  config: [ASSETS, 'config'] as const,
  folders: [ASSETS, 'folders'] as const,
  unused: (params: AssetListParams) => [ASSETS, 'unused', params] as const,
};

/** Invalidate everything that a byte-level or metadata change can move. */
function invalidateLibrary(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: assetKeys.all });
}

// ─── Reads ────────────────────────────────────────────────────────────────────

export function useAssets(params: AssetListParams, enabled = true) {
  return useQuery({
    queryKey: assetKeys.list(params),
    queryFn: () => assetsService.list(params),
    enabled,
    // Keep the previous page on screen while the next one loads, so the grid
    // does not collapse to a spinner on every keystroke of the search box.
    placeholderData: (prev) => prev,
  });
}

export function useAsset(id: string | null | undefined) {
  return useQuery({
    queryKey: assetKeys.detail(id || ''),
    queryFn: () => assetsService.get(id as string),
    enabled: Boolean(id),
  });
}

export function useAssetUsage(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: assetKeys.usage(id || ''),
    queryFn: () => assetsService.usage(id as string),
    enabled: Boolean(id) && enabled,
  });
}

export function useAssetStats(params: AssetListParams = {}) {
  return useQuery({
    queryKey: assetKeys.stats(params),
    queryFn: () => assetsService.stats(params),
  });
}

/**
 * Upload limits and accepted formats, straight from the server.
 * Long-lived on purpose — these only change on a deploy.
 */
export function useAssetConfig() {
  return useQuery({
    queryKey: assetKeys.config,
    queryFn: () => assetsService.config(),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export function useAssetFolders() {
  return useQuery({
    queryKey: assetKeys.folders,
    queryFn: () => assetsService.folders(),
  });
}

export function useUnusedAssets(params: AssetListParams = {}, enabled = true) {
  return useQuery({
    queryKey: assetKeys.unused(params),
    queryFn: () => assetsService.unused(params),
    enabled,
  });
}

// ─── Writes ───────────────────────────────────────────────────────────────────

export function useUpdateAssetMetadata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssetMetadataPayload }) =>
      assetsService.updateMetadata(id, data),
    onSuccess: (asset) => {
      qc.setQueryData(assetKeys.detail(asset.id), asset);
      invalidateLibrary(qc);
      toast.success('Image details saved');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Delete one asset.
 *
 * The 409 is NOT an error to swallow: it carries the list of records still
 * pointing at the image. Callers pass `onConflict` to show them and offer the
 * force path; without a handler we fall back to a plain toast.
 */
export function useDeleteAsset(
  onConflict?: (conflict: NonNullable<ReturnType<typeof asAssetInUseConflict>>) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      assetsService.remove(id, force ?? false),
    onSuccess: (_result, variables) => {
      qc.removeQueries({ queryKey: assetKeys.detail(variables.id) });
      invalidateLibrary(qc);
      toast.success('Image deleted');
    },
    onError: (error) => {
      const conflict = asAssetInUseConflict(error);
      if (conflict && onConflict) {
        onConflict(conflict);
        return;
      }
      toast.error(conflict ? conflict.detail : getErrorMessage(error));
    },
  });
}

export function useReplaceAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      onProgress,
    }: {
      id: string;
      data: AssetReplacePayload;
      onProgress?: (percent: number) => void;
    }) => assetsService.replace(id, data, onProgress),
    onSuccess: (asset) => {
      qc.setQueryData(assetKeys.detail(asset.id), asset);
      invalidateLibrary(qc);
      toast.success(
        asset.url_changed
          ? 'Image replaced. The direct file URL changed — pages using the permanent link are unaffected.'
          : 'Image replaced everywhere it is used',
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useRegenerateVariants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetsService.regenerateVariants(id),
    onSuccess: (result) => {
      qc.setQueryData(assetKeys.detail(result.asset.id), result.asset);
      invalidateLibrary(qc);
      toast.success(`Rebuilt ${result.generated} rendition${result.generated === 1 ? '' : 's'}`);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Bulk clean-up. The server skips in-use rows rather than deleting them, so a
 * partial result is the normal case and must be reported honestly.
 */
export function useDeleteUnusedAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids?: string[]; folder?: string }) => assetsService.deleteUnused(body),
    onSuccess: (result) => {
      invalidateLibrary(qc);
      const skipped = result.skipped.length;
      if (!result.deleted_count && skipped) {
        toast.error(`Nothing deleted — ${skipped} image${skipped === 1 ? ' is' : 's are'} still in use.`);
        return;
      }
      toast.success(
        skipped
          ? `Deleted ${result.deleted_count}; kept ${skipped} still in use.`
          : `Deleted ${result.deleted_count} unused image${result.deleted_count === 1 ? '' : 's'}`,
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

// ─── Folders ──────────────────────────────────────────────────────────────────

export function useCreateAssetFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FolderPayload) => assetsService.createFolder(payload),
    onSuccess: () => {
      invalidateLibrary(qc);
      toast.success('Folder created');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useUpdateAssetFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FolderPayload> }) =>
      assetsService.updateFolder(id, data),
    onSuccess: () => {
      invalidateLibrary(qc);
      toast.success('Folder renamed');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useDeleteAssetFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetsService.deleteFolder(id),
    onSuccess: () => {
      invalidateLibrary(qc);
      toast.success('Folder deleted. The images in it were kept and are now unfiled.');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}
